import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Drawer } from '../../../../packages/ui/organisms/drawer';
import {
  btnType,
  Button,
  fontWeight,
  Heading,
  headingLevel,
  IconButton,
  Input,
  inputSize,
  Select,
  selectType,
  Text,
  Textarea,
  TextError,
} from '../../../../packages';
import { sxStyles, useStyles } from './AddConstructs.styles';
import { createConstruct, updateConstruct } from '../../../../services/profile/profile.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';

import { AddConstructsSchema } from './AddConstructs.schema';

export const AddConstructs = ({ isOpen, onClose, selectedConstruct, isEditMode, onRefresh }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const defaultValues = {
    constructName: selectedConstruct?.name || '',
    prompt: selectedConstruct?.prompt || '',
    description: selectedConstruct?.description || '',
    clinicalImportance: selectedConstruct?.clinicalImportance || 'MEDIUM',
    updateStrategy: selectedConstruct?.updateStrategy || 'REPLACE',
  };

  const { errors, values, handleChange, handleSubmit, touched, setFieldValue, ...rest } = useFormik({
    initialValues: { ...defaultValues },
    enableReinitialize: true,
    validationSchema: AddConstructsSchema,
    onSubmit: async payload => {
      await handleFormSubmit(payload);
    },
  });

  const setTouched = name => async () => {
    await rest.setTouched({ ...touched, [name]: true });
  };

  useEffect(() => {
    if (!isOpen) {
      rest.handleReset({ ...defaultValues });
      rest.setErrors({});
      setError(null);
    }
  }, [isOpen]);

  const handleFormSubmit = async values => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        name: values.constructName,
        prompt: values.prompt,
        description: values.description,
        clinicalImportance: values.clinicalImportance,
        updateStrategy: values.updateStrategy,
        trigger: 'TRANSCRIPT', // Hardcoded value for now
      };

      if (isEditMode && selectedConstruct?.id) {
        const updatePayload = {
          ...payload,
          id: selectedConstruct.id,
          ...selectedConstruct,
          name: values.constructName,
          prompt: values.prompt,
          description: values.description,
          clinicalImportance: values.clinicalImportance,
          updateStrategy: values.updateStrategy,
          trigger: 'TRANSCRIPT', // Hardcoded value for now
        };
        await updateConstruct(updatePayload, { id: selectedConstruct.id });

        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Construct updated successfully',
            duration: 3000,
          }),
        );
      } else {
        await createConstruct(payload);

        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Construct added successfully',
            duration: 3000,
          }),
        );
      }

      onClose();
      onRefresh && onRefresh();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} construct:`, err);

      if (err?.status === 422 || err?.response?.status === 422) {
        setError('Construct with this name already exist');
      } else {
        setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} construct`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderLabel = label => (
    <Heading level={headingLevel.S} className={classes.label} weight={fontWeight.BOLD}>
      {label}
    </Heading>
  );

  const clinicalImportanceTypes = [
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' },
  ];
  const updateStrategyTypes = [
    { label: 'Hybrid', value: 'HYBRID' },
    { label: 'Integrate', value: 'INTEGRATE' },
    { label: 'Replace', value: 'REPLACE' },
  ];

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} className={classes.drawer}>
        <Box sx={sxStyles.wrapper}>
          <Box sx={sxStyles.header}>
            <Text className={classes.headerTitle}>{isEditMode ? 'Update construct' : 'Add construct'}</Text>
            <IconButton icon="close" onClick={onClose} sx={sxStyles.backBtn} />
          </Box>
          {error && (
            <Box sx={sxStyles.errorContainer}>
              <TextError errorMsg={error} />
            </Box>
          )}
          <form className={classes.form} onSubmit={handleSubmit}>
            <Box sx={sxStyles.formContent}>
              <Box sx={sxStyles.section}>
                {renderLabel('Construct name')}
                <Input
                  fullWidth
                  name="constructName"
                  value={values.constructName}
                  placeholder="Enter construct name"
                  size={inputSize.M}
                  onBlur={setTouched('constructName')}
                  onChange={handleChange}
                  error={touched.constructName && errors.constructName}
                />
                <TextError errorMsg={touched.constructName ? errors.constructName?.toString() : null} />
              </Box>
              <Box sx={sxStyles.section}>
                {renderLabel('Prompt')}
                <Textarea
                  name="prompt"
                  value={values.prompt}
                  onChange={handleChange}
                  onBlur={setTouched('prompt')}
                  placeholder="Enter prompt"
                  minRows={5}
                />
              </Box>
              <Box sx={sxStyles.section}>
                {renderLabel('Description')}
                <Input
                  fullWidth
                  name="description"
                  value={values.description}
                  placeholder="Enter description"
                  size={inputSize.M}
                  onBlur={setTouched('description')}
                  onChange={handleChange}
                  error={touched.description && errors.description}
                />
                <TextError errorMsg={touched.description ? errors.description?.toString() : null} />
              </Box>
              <Box sx={sxStyles.section}>
                {renderLabel('Clinical importance')}
                <Select
                  fullWidth
                  displayEmpty
                  name="clinicalImportance"
                  value={values.clinicalImportance}
                  options={clinicalImportanceTypes}
                  emptyText="Select type"
                  variant={selectType.SECONDARY}
                  onBlur={setTouched('clinicalImportance')}
                  onChange={handleChange}
                  error={touched.clinicalImportance && errors.clinicalImportance}
                />
                <TextError
                  errorMsg={touched.clinicalImportance ? errors.clinicalImportance?.toString() : null}
                />
              </Box>
              <Box sx={sxStyles.section}>
                {renderLabel('Update strategy')}
                <Select
                  fullWidth
                  displayEmpty
                  name="updateStrategy"
                  value={values.updateStrategy}
                  options={updateStrategyTypes}
                  emptyText="Select type"
                  variant={selectType.SECONDARY}
                  onBlur={setTouched('updateStrategy')}
                  onChange={handleChange}
                  error={touched.updateStrategy && errors.updateStrategy}
                />
                <TextError errorMsg={touched.updateStrategy ? errors.updateStrategy?.toString() : null} />
              </Box>
            </Box>
            <Box sx={sxStyles.footer}>
              <Button variant={btnType.TEXT} onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={loading} loading={loading}>
                {isEditMode ? 'Update construct' : 'Add construct'}
              </Button>
            </Box>
          </form>
        </Box>
      </Drawer>
    </>
  );
};

export default AddConstructs;
